'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { Title } from './Title';

const HEADER_HEIGHT = 72; // consistent height for desktop + mobile

// Routes the admin has disabled disappear from the navigation (passed down
// from the root layout, which reads the site_pages store).
const Header = ({ disabledRoutes = [] }: { disabledRoutes?: string[] }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const pathname = usePathname();
  // Highlight the current section, incl. nested routes (e.g. /products/[slug]).
  const isActive = (href: string) => pathname === href || pathname.startsWith(`${href}/`);

  const navigationItems = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    // Sits directly after Products because that is the order a UK buyer reads
    // in: what you sell, then whether you can actually land it here. The label
    // matches the page's own H1 and the footer link  - a nav item that renames
    // its destination costs more than the characters it saves.
    { label: 'Supplying the UK', href: '/supplying-the-uk' },
    { label: 'About', href: '/about' },
    { label: 'Insights', href: '/insights' },
    { label: 'FAQ', href: '/faq' },
    { label: 'Contact Us', href: '/contact' },
  ].filter((item) => !disabledRoutes.includes(item.href));

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : 'unset';
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`
        fixed top-0 left-0 right-0 z-50 bg-primary
        transition-shadow duration-300 ease-confident
        ${isScrolled ? 'shadow-lg shadow-primary/25' : ''}
      `}
      style={{ height: HEADER_HEIGHT }}
    >
      {/* Match the page content container (max-w-7xl + padding) so the logo lines
          up with the content below and the nav/CTA don't spill to the edges. */}
      <div className="mx-auto flex h-full max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
        <Title />

        {/* Desktop Navigation - sits between the brand and the CTA (justify-between
            pulls it toward the centre, filling the old blank gap). */}
        <nav className="hidden items-center gap-6 lg:flex xl:gap-8">
          {navigationItems.map((item) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href + item.label}
                href={item.href}
                aria-current={active ? 'page' : undefined}
                className={`font-cta text-base font-bold transition-colors duration-300 relative group ${
                  active ? 'text-secondary' : 'text-primary-foreground hover:text-secondary'
                }`}
              >
                {item.label}
                <span
                  className={`absolute -bottom-1 left-0 w-full h-0.375 bg-secondary origin-left transition-transform duration-300 ${
                    active ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            );
          })}
        </nav>

        {/* Right side: primary CTA (desktop) + mobile menu button */}
        <div className="flex items-center gap-3">
          <Link
            href="/products?inquiry=Sample"
            className="hidden items-center gap-2 rounded-xl bg-secondary px-4 py-2.5 font-cta text-sm font-bold text-primary transition duration-300 hover:bg-secondary/90 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-primary xl:inline-flex"
          >
            <Icon name="CubeIcon" size={16} aria-hidden="true" />
            Request a Sample
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden inline-flex items-center justify-center min-w-11 min-h-11 -mr-2 text-primary-foreground hover:text-secondary transition-colors duration-300"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={28} />
          </button>
        </div>
      </div>

      {/* Mobile Menu Backdrop  - dims page + dismiss on outside tap */}
      <div
        onClick={() => setIsMobileMenuOpen(false)}
        aria-hidden="true"
        className={`lg:hidden fixed inset-0 z-40 bg-foreground/40 transition-opacity duration-300 ease-out ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible pointer-events-none'
        }`}
      />

      {/* Mobile Menu Overlay */}
      <div
        id="mobile-menu"
        className={`
          lg:hidden fixed left-0 right-0 bottom-0 z-50
          transition duration-300 ease-confident
          ${isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}
        `}
        style={{ top: HEADER_HEIGHT }}
      >
        <nav className="flex flex-col p-6 gap-1 border-b border-border bg-surface shadow-dramatic max-h-[calc(100vh-72px)] overflow-y-auto">
          {navigationItems.map((item, index) => {
            const active = isActive(item.href);
            return (
              <Link
                key={item.href + item.label}
                href={item.href}
                onClick={() => setIsMobileMenuOpen(false)}
                aria-current={active ? 'page' : undefined}
                className={`flex items-center min-h-11 py-1 px-4 font-cta font-bold text-base rounded-md transition duration-300 active:scale-95 ${
                  active
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground hover:bg-foreground/5 active:bg-foreground/10'
                }`}
                style={{
                  animation: isMobileMenuOpen
                    ? `slideIn 0.3s ease-out ${index * 0.08}s both`
                    : 'none',
                }}
              >
                {item.label}
              </Link>
            );
          })}

          {/* Primary CTA, matching the desktop header */}
          <Link
            href="/products?inquiry=Sample"
            onClick={() => setIsMobileMenuOpen(false)}
            className="mt-3 inline-flex min-h-11 items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 font-cta font-bold text-primary-foreground transition duration-300 active:scale-95"
          >
            <Icon name="CubeIcon" size={18} aria-hidden="true" />
            Request a Sample
          </Link>
        </nav>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </header>
  );
};

export default Header;
